// Single issue detail hook — issue + conversations + attachments + assignments + validations
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'

const ISSUE_DETAIL_KEY = 'issue-detail'

export function useIssueDetail(issueId: string | undefined) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const issueQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId],
    queryFn: async () => {
      if (!issueId) throw new Error('No issue ID')

      const { data, error } = await supabase
        .from('issues')
        .select('*')
        .eq('id', issueId)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!issueId && !!user,
  })

  const issueData = issueQuery.data as Record<string, unknown> | undefined

  const customerQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId, 'customer'],
    queryFn: async () => {
      const custId = issueData?.customer_id as string | null
      if (!custId) return null
      const { data } = await supabase.from('customers').select('*').eq('id', custId).single()
      return data
    },
    enabled: !!issueData?.customer_id,
  })

  const entityQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId, 'entity'],
    queryFn: async () => {
      const entId = issueData?.entity_id as string | null
      if (!entId) return null
      const { data } = await supabase.from('customer_entities').select('*').eq('id', entId).single()
      return data
    },
    enabled: !!issueData?.entity_id,
  })

  const reportedByQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId, 'reported_by'],
    queryFn: async () => {
      const userId = issueData?.customer_user_id as string | null
      if (!userId) return null
      const { data } = await supabase.from('customer_users').select('*').eq('id', userId).single()
      return data
    },
    enabled: !!issueData?.customer_user_id,
  })

  const conversationsQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId, 'conversations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('issue_conversations')
        .select('*')
        .eq('issue_id', issueId!)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data ?? []
    },
    enabled: !!issueId && !!user,
  })

  // Query documents linked to this issue via source_id or uploaded during chat
  const attachmentsQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId, 'attachments'],
    queryFn: async () => {
      // Documents uploaded during issue recording are in customer_profile_documents
      // linked by issue_id column or by category 'issue_attachment'
      const { data, error } = await supabase
        .from('customer_profile_documents')
        .select('id, file_name, file_type, file_size_bytes, storage_path, storage_bucket, created_at, category, processing_status, title')
        .eq('issue_id', issueId!)
        .order('created_at', { ascending: false })

      if (error) {
        // Fallback: issue_id column may not exist yet, try without filter
        console.warn('Attachments query failed, trying without issue_id filter:', error.message)
        return []
      }
      return data ?? []
    },
    enabled: !!issueId && !!user,
  })

  const assignmentsQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId, 'assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('expert_assignments')
        .select('*, expert_profiles(*)')
        .eq('issue_id', issueId!)
        .order('assigned_at', { ascending: false })

      if (error) throw error
      return data ?? []
    },
    enabled: !!issueId && !!user,
  })

  const validationsQuery = useQuery({
    queryKey: [ISSUE_DETAIL_KEY, issueId, 'validations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('solution_validations')
        .select('*')
        .eq('issue_id', issueId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data ?? []
    },
    enabled: !!issueId && !!user,
  })

  const addCommentMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!issueId || !user) throw new Error('Missing context')
      const { error } = await supabase.from('issue_conversations').insert({
        issue_id: issueId,
        organization_id: user.organizationId,
        user_id: user.id,
        role: 'user',
        content,
        is_internal: false,
        metadata: {},
      } as never)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ISSUE_DETAIL_KEY, issueId, 'conversations'] })
    },
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: [ISSUE_DETAIL_KEY, issueId] })
  }

  return {
    issue: issueQuery.data,
    customer: customerQuery.data ?? null,
    entity: entityQuery.data ?? null,
    reportedBy: reportedByQuery.data ?? null,
    conversations: conversationsQuery.data ?? [],
    attachments: attachmentsQuery.data ?? [],
    assignments: assignmentsQuery.data ?? [],
    validations: validationsQuery.data ?? [],
    isLoading: issueQuery.isLoading,
    error: issueQuery.error,
    addComment: addCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isPending,
    invalidateAll,
  }
}
